import React, { useEffect, useState } from 'react';
import { addCoupon, deleteCoupon, getAllCoupons, updateCouponCount } from '../../actions/admin';
import { useSelector } from 'react-redux';
import {
  Box,
  Grid,
  IconButton,
  TextField,
  Typography,
  styled,
  Button,
  FormHelperText,
  Backdrop,
  CircularProgress,
  Checkbox,
  Card,
  CardContent,
  Container,
  Fade,
  Zoom,
  Snackbar,
  Alert,
  Chip,
} from '@mui/material';
import { DeleteForever, LocalOffer, Add, Percent, CurrencyRupee, Code } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import Select from 'react-select';
import "./promo-codes.css"

const StyledContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  paddingTop: '2rem',
  paddingBottom: '2rem',
  width: '100%',
}));

const GlassCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  borderRadius: '20px',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
  }
}));

const PromoCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  borderRadius: '12px',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  height: 'fit-content',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
  }
}));

const FormCard = styled(GlassCard)(({ theme }) => ({
  padding: '2rem',
  maxWidth: '500px',
  margin: '0 auto',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    background: 'rgba(255, 255, 255, 0.8)',
    '& fieldset': {
      borderColor: 'rgba(103, 126, 234, 0.3)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(103, 126, 234, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#667eea',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#667eea',
    fontWeight: '500',
  },
}));

const NumberTextField = styled(StyledTextField)({
  '& input': {
    height: '40px',
    padding: '0px 14px !important',
    fontSize: '1rem',
  }
});

const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  borderRadius: '12px',
  padding: '12px 24px',
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: '600',
  boxShadow: '0 4px 15px rgba(103, 126, 234, 0.4)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(103, 126, 234, 0.6)',
  }
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '12px',
  right: '12px',
  background: 'rgba(244, 67, 54, 0.1)',
  color: '#f44336',
  '&:hover': {
    background: 'rgba(244, 67, 54, 0.2)',
    transform: 'scale(1.1)',
  },
  transition: 'all 0.2s ease',
}));

const Label = styled(Typography)({
  marginBottom: '8px',
  fontWeight: '600',
  color: '#374151',
  fontSize: '0.95rem',
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: 'white',
  fontWeight: '700',
  marginBottom: '2rem',
  textAlign: 'center',
  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
}));

const CodeChip = styled(Chip)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  fontWeight: '600',
  fontSize: '0.85rem',
  padding: '4px 2px',
  borderRadius: '8px',
  height: '32px',
  '& .MuiChip-label': {
    paddingLeft: '8px',
    paddingRight: '8px',
  }
}));

const DiscountChip = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2px',
  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  color: 'white',
  padding: '4px 8px',
  borderRadius: '16px',
  fontSize: '0.8rem',
  fontWeight: '600',
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '8px',
  padding: '6px 10px',
  background: 'rgba(103, 126, 234, 0.05)',
  borderRadius: '6px',
  border: '1px solid rgba(103, 126, 234, 0.1)',
}));

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    borderRadius: '12px',
    border: '1px solid rgba(103, 126, 234, 0.3)',
    background: 'rgba(255, 255, 255, 0.9)',
    minHeight: '56px',
    boxShadow: 'none',
    '&:hover': {
      borderColor: 'rgba(103, 126, 234, 0.5)',
    }
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'white',
    border: '1px solid rgba(103, 126, 234, 0.2)',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
    zIndex: 9999,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: '8px',
    borderRadius: '12px',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#667eea' : state.isFocused ? 'rgba(103, 126, 234, 0.1)' : 'transparent',
    color: state.isSelected ? 'white' : '#374151',
    borderRadius: '8px',
    margin: '2px 0',
    fontWeight: '500',
    '&:hover': {
      backgroundColor: state.isSelected ? '#667eea' : 'rgba(103, 126, 234, 0.1)',
    }
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#374151',
    fontWeight: '500',
  })
};

const PromoCodes = () => {
  const { token } = useSelector(state => state.userSlice);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm({
    defaultValues: {
      code:'',
      count:'',
      discount_type: 'flat',
      discount: '',
      max_discount: '',
      min_amount: '',
      is_premium: false
    }
  });

  const discountType = watch('discount_type');
  const isPremium = watch('is_premium');

  const fetchCoupons = async () => {
    const resp = await getAllCoupons({ token });
    setCoupons(resp?.coupons || []);
  };
  
  const deleteOneCoupon = async id => {
    setLoading(true);
    try {
      await deleteCoupon({ token, id });
      // await updateCouponCount({ token, id });
      await fetchCoupons();
      setToast({ open: true, message: 'Promo code deleted successfully!', severity: 'success' });
    } catch (error) {
      setToast({ open: true, message: 'Failed to delete promo code', severity: 'error' });
    }
    setLoading(false);
  };

  const addOneCoupon = async data => {
    setLoading(true);
    try {
      await addCoupon({ token, data });
      await fetchCoupons();
      reset();
      setToast({ open: true, message: 'Promo code added successfully!', severity: 'success' });
    } catch (error) {
      setToast({ open: true, message: 'Failed to add promo code', severity: 'error' });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  return (
    <StyledContainer>
      <Container maxWidth="xl">
        <Backdrop
          open={loading}
          sx={{
            zIndex: 1010,
            backdropFilter: 'blur(4px)',
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ color: '#667eea' }} />
        </Backdrop>

        <Grid container spacing={4}>
          {/* All Promo Codes Section */}
          <Grid item xs={12} lg={6}>
            <SectionTitle variant='h4'>
              <LocalOffer sx={{ mr: 1, verticalAlign: 'middle' }} />
              All Promo Codes
            </SectionTitle>
            
            <Grid container spacing={2}>
              {coupons.map((coupon, index) => (
                <Grid item xs={12} sm={6} key={coupon._id}>
                  <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                    <PromoCard>
                      <DeleteButton
                        onClick={() => deleteOneCoupon(coupon._id)}
                        size="small"
                      >
                        <DeleteForever />
                      </DeleteButton>
                      
                      <CardContent sx={{ p: 2, pr: 5 }}>
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CodeChip 
                            label={coupon.code} 
                            icon={<Code sx={{ fontSize: '0.9rem !important' }} />}
                          />
                          {coupon.is_premium && (
                            <Chip 
                              label="Premium" 
                              size="small"
                              sx={{ 
                                background: '#fbbf24', 
                                color: 'white',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                height: '24px'
                              }} 
                            />
                          )}
                        </Box>

                        <InfoRow>
                          <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                            Type:
                          </Typography>
                          <Typography variant="body2" fontWeight="600" sx={{ textTransform: 'capitalize' }}>
                            {coupon.discount_type}
                          </Typography>
                        </InfoRow>

                        <InfoRow>
                          <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                            Discount:
                          </Typography>
                          <DiscountChip>
                            {coupon.discount_type === 'flat' ? (
                              <><CurrencyRupee sx={{ fontSize: '0.8rem' }} />{coupon.discount}</>
                            ) : (
                              <>{coupon.discount}<Percent sx={{ fontSize: '0.8rem' }} /></>
                            )}
                          </DiscountChip>
                        </InfoRow>

                        <InfoRow>
                          <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                            Count:
                          </Typography>
                          <Typography variant="body2" fontWeight="700" color="#667eea">
                            {coupon.count}
                          </Typography>
                        </InfoRow>

                        {coupon?.discount_type === 'percent' && (
                          <InfoRow>
                            <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                              Max:
                            </Typography>
                            <Typography variant="body2" fontWeight="600">
                              ₹{coupon.max_discount}
                            </Typography>
                          </InfoRow>
                        )}

                        <InfoRow>
                          <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                            Min:
                          </Typography>
                          <Typography variant="body2" fontWeight="600">
                            ₹{coupon.min_amount}
                          </Typography>
                        </InfoRow>
                      </CardContent>
                    </PromoCard>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Add New Promo Code Section */}
          <Grid item xs={12} lg={6}>
            <SectionTitle variant='h4'>
              <Add sx={{ mr: 1, verticalAlign: 'middle' }} />
              Add New Promo Code
            </SectionTitle>
            
            <Fade in={true} timeout={800}>
              <FormCard>
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Label>Promo Code</Label>
                      <StyledTextField
                        fullWidth
                        placeholder='Enter the code'
                        {...register('code', { required: 'Code is required!' })}
                        error={!!errors?.code}
                      />
                      {errors?.code && (
                        <FormHelperText error sx={{ mt: 1 }}>
                          {errors?.code?.message}
                        </FormHelperText>
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <Label>Usage Count</Label>
                      <NumberTextField
                        type='number'
                        fullWidth
                        placeholder='Enter the count of the code'
                        {...register('count', {
                          required: 'Count is required!',
                          min: {
                            value: 1,
                            message: 'Count must be greater than 0'
                          }
                        })}
                        error={!!errors?.count}
                      />
                      {errors?.count && (
                        <FormHelperText error sx={{ mt: 1 }}>
                          {errors?.count?.message}
                        </FormHelperText>
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <Label>Discount Type</Label>
                      <Select
                        options={['flat', 'percent'].map(opt => ({
                          label: opt[0].toUpperCase() + opt.slice(1),
                          value: opt
                        }))}
                        onChange={val => setValue('discount_type', val.value)}
                        value={{
                          label: discountType[0].toUpperCase() + discountType.slice(1),
                          value: discountType
                        }}
                        styles={customSelectStyles}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Label>
                        Discount (in {`${discountType === 'flat' ? 'Rupees' : 'Percent'}`})
                      </Label>
                      <NumberTextField
                        type='number'
                        fullWidth
                        placeholder={`Enter discount ${discountType === 'flat' ? 'amount' : 'percentage'}`}
                        InputProps={{
                          startAdornment: discountType === 'flat' ? 
                            <CurrencyRupee sx={{ color: '#667eea', mr: 1 }} /> : 
                            <Percent sx={{ color: '#667eea', mr: 1 }} />,
                        }}
                        {...register('discount', {
                          required: 'Discount is required!',
                          min: {
                            value: 1,
                            message: 'Discount must be greater than 0'
                          }
                        })}
                        error={!!errors?.discount}
                      />
                      {errors?.discount && (
                        <FormHelperText error sx={{ mt: 1 }}>
                          {errors?.discount?.message}
                        </FormHelperText>
                      )}
                    </Grid>

                    {discountType === 'percent' && (
                      <Grid item xs={12}>
                        <Label>Maximum Discount (in Rupees)</Label>
                        <NumberTextField
                          type='number'
                          fullWidth
                          placeholder='Enter maximum discount amount'
                          InputProps={{
                            startAdornment: <CurrencyRupee sx={{ color: '#667eea', mr: 1 }} />,
                          }}
                          {...register('max_discount', {
                            required: 'Maximum discount is required!',
                            min: {
                              value: 1,
                              message: 'Maximum discount must be greater than 0'
                            }
                          })}
                          error={!!errors?.max_discount}
                        />
                        {errors?.max_discount && (
                          <FormHelperText error sx={{ mt: 1 }}>
                            {errors?.max_discount?.message}
                          </FormHelperText>
                        )}
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Label>Minimum Amount (in Rupees)</Label>
                      <NumberTextField
                        type='number'
                        fullWidth
                        placeholder='Enter minimum order amount'
                        InputProps={{
                          startAdornment: <CurrencyRupee sx={{ color: '#667eea', mr: 1 }} />,
                        }}
                        {...register('min_amount', {
                          required: 'Minimum amount is required!',
                          min: {
                            value: 1,
                            message: 'Minimum amount must be greater than 0'
                          }
                        })}
                        error={!!errors?.min_amount}
                      />
                      {errors?.min_amount && (
                        <FormHelperText error sx={{ mt: 1 }}>
                          {errors?.min_amount?.message}
                        </FormHelperText>
                      )}
                    </Grid>
                    
                    <Grid item xs={12}>
                      <GradientButton
                        fullWidth
                        variant='contained'
                        size="large"
                        onClick={handleSubmit(addOneCoupon)}
                        startIcon={<Add />}
                      >
                        Add Promo Code
                      </GradientButton>
                    </Grid>
                  </Grid>
                </CardContent>
              </FormCard>
            </Fade>
          </Grid>
        </Grid>

        {/* Toast Notification */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast({ ...toast, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setToast({ ...toast, open: false })}
            severity={toast.severity}
            variant="filled"
            sx={{
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </StyledContainer>
  );
};

export default PromoCodes;